import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { parseGtfsId } from '#/shared/utils/gtfs-id';
import tripPlannerConfig from '#/trip-planner-config';
import { ROUTE_COLOR_QUERY } from '#/trip-planner/graphql/queries';
import { DEPARTURES_FOR_STOP_QUERY } from '#/transit-tracker/graphql/queries';
import type {
  Route,
  StopTimesQuery,
  StopTimesQueryVariables,
} from '#/types/otp2types.generated';
import type { ContrastLookup } from '#/types/contrast';
import type { Route as TrimetStyleRoute } from '#/types/Routes';
import { CACHE_DURATION, NODE_ENVS } from '#/shared/constants';
import {
  calculateContrastColors,
  normalizeHex,
} from '#/shared/utils/colorContrast';

const { api } = tripPlannerConfig;

// GraphQL response types
type RouteColor = Pick<Route, 'gtfsId' | 'color'>;

interface GraphQLResponse<T> {
  data?: T;
  errors?: { message: string }[];
}

interface RouteColorsData {
  routes: RouteColor[];
}

export interface RouteColorsResponse {
  contrastLookup: ContrastLookup;
}

interface DeparturesForStopResponse {
  departureMilliseconds: number;
  headsign: string;
  id: string;
  route: TrimetStyleRoute;
}

export const graphqlApi = createApi({
  reducerPath: 'graphqlApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${api.host}${api.paths.plan}`,
    prepareHeaders: headers => {
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  endpoints: build => ({
    getRouteColors: build.query<RouteColorsResponse, undefined>({
      query: () => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: ROUTE_COLOR_QUERY,
        }),
      }),
      transformResponse: (response: GraphQLResponse<RouteColorsData>) => {
        const routes = response.data?.routes ?? [];

        // Normalize colors and ensure they have a leading '#'
        const uniqueColors = new Set<string>(
          routes
            .map(route => normalizeHex(route.color))
            .filter((colorHex): colorHex is string => Boolean(colorHex))
        );

        // Calculate contrast adjustments for each unique color
        const contrastLookup: ContrastLookup = {};

        uniqueColors.forEach(colorHex => {
          try {
            contrastLookup[colorHex] = calculateContrastColors(colorHex);
          } catch (e) {
            if (process.env.NODE_ENV !== NODE_ENVS.PROD) {
              console.warn('calculateContrastColors failed for', colorHex, e);
            }
            contrastLookup[colorHex] = { light: null, dark: null };
          }
        });

        // Include colors where either light OR dark is populated
        const filteredContrastLookup: ContrastLookup = Object.fromEntries(
          Object.entries(contrastLookup).filter(
            ([, v]) => v.light !== null || v.dark !== null
          )
        );

        return {
          contrastLookup: filteredContrastLookup,
        };
      },
      keepUnusedDataFor: CACHE_DURATION.ONE_HOUR,
    }),
    getDeparturesForStop: build.query<
      DeparturesForStopResponse[] | null,
      StopTimesQueryVariables
    >({
      query: params => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: DEPARTURES_FOR_STOP_QUERY,
          variables: params,
        }),
      }),
      transformResponse: (response: GraphQLResponse<StopTimesQuery>) => {
        const stopTimes = response.data?.stop?.stoptimesForServiceDate?.flatMap(
          stoptimesInPattern => stoptimesInPattern?.stoptimes
        );

        if (!stopTimes) {
          return null;
        }

        const transformedStopTimes = stopTimes
          .map(stopTime => {
            if (!stopTime?.trip || !stopTime?.headsign) {
              return null;
            }

            const {
              scheduledDeparture,
              serviceDay,
              headsign,
              trip: {
                id,
                route: { gtfsId, shortName, longName, desc },
              },
            } = stopTime;
            if (
              // Can't just check for truthiness here because of 0
              typeof scheduledDeparture !== 'number' ||
              typeof serviceDay !== 'number'
            ) {
              return null;
            }

            const parsedId = parseGtfsId(gtfsId);

            if (!parsedId) {
              return null;
            }

            const departureMilliseconds =
              (scheduledDeparture + serviceDay) * 1000;

            return {
              departureMilliseconds,
              headsign,
              id,
              route: {
                // GraphQL returns null if it doesn't have information for a requested field,
                // so have to coalesce to undefined to match our types
                routeLongName: longName ?? undefined,
                routeShortName: shortName ?? undefined,
                desc: desc ?? undefined,
                id: parsedId.id,
                feedId: parsedId.feedId,
              },
            };
          })
          .filter(val => val !== null)
          .sort((a, b) => a.departureMilliseconds - b.departureMilliseconds);

        return transformedStopTimes;
      },
    }),
  }),
});

export const { useGetRouteColorsQuery, useGetDeparturesForStopQuery } =
  graphqlApi;
